// @flow

import React, { Component } from 'react';
import i18n from 'app/common/lib/i18n';
import millisToMinutesAndSeconds from 'app/common/lib/util/millisToMinutesAndSeconds';
import PrettyNumber from 'app/common/react/i18n/PrettyNumber';
import type {
  ListCreatorAnalytics,
  StatsType,
} from 'app/common/react/components/closeup/PinAnalytics';
import { Box, Icon, Text } from 'gestalt';

type Props = {
  stats: ListCreatorAnalytics,
  isVideo: boolean,
  isStoryPin: boolean,
  show90Days: boolean,
};

type ValueFormat = 'TIME' | 'COUNT';

type ExtendedStats = {|
  ...StatsType,
  align: 'start' | 'center' | 'end',
  valueFormat?: ValueFormat,
|};

export default class PinStatsOnGrid extends Component<Props> {
  statsList: Array<ExtendedStats> = this.props.isVideo
    ? [
        {
          iconType: 'play',
          displayText: i18n._('Video views', 'Label for views of a video pin on closeup page'),
          field: 'video_views',
          align: 'start',
        },
        {
          iconType: 'clock',
          displayText: i18n._('Avg. time', 'Label for average time of a video pin on closeup page'),
          field: 'video_average_time',
          align: 'center',
          valueFormat: 'TIME',
        },
        {
          iconType: 'angled-pin',
          displayText: i18n._('Saves', 'Label for saves of a video pin on closeup page'),
          field: 'save',
          align: 'end',
        },
      ]
    : [
        {
          iconType: 'eye',
          displayText: i18n._('Impressions', 'Label for impressions of a pin on closeup page'),
          field: 'impression',
          align: 'start',
        },
        {
          iconType: 'angled-pin',
          displayText: i18n._('Saves', 'Label for saves of a pin on closeup page'),
          field: 'save',
          align: 'center',
        },
        {
          iconType: 'arrow-up-right',
          displayText: i18n._('Link clicks', 'Label for click-throughs of a pin on closeup page'),
          field: 'clickthrough',
          iconSize: 10,
          align: 'end',
        },
      ];

  renderValue(value: number, valueFormat: ValueFormat = 'COUNT') {
    if (Number.isInteger(value)) {
      return valueFormat === 'TIME' ? (
        millisToMinutesAndSeconds(value)
      ) : (
        <PrettyNumber value={value} shortform shortformMaximumFractionDigits={1} />
      );
    } else {
      return '-';
    }
  }

  render() {
    const { isVideo, stats, isStoryPin, show90Days } = this.props;

    if (!stats) {
      return null;
    }
    const defaultStatsProp = show90Days ? '90d' : '30d';
    const renderedStats =
      (isVideo || isStoryPin) && !!stats.all_time ? stats.all_time : stats[defaultStatsProp];
    if (!renderedStats || Object.keys(renderedStats).length === 0) {
      return null;
    }

    return (
      <Box display="flex">
        {this.statsList.map(
          ({ align, iconType, iconSize = 12, field, displayText, valueFormat }, index) => (
            <Box key={index} column={4} display="flex" justifyContent={align} alignItems="center">
              <Box display="inlineBlock" marginRight={1}>
                <Icon
                  accessibilityLabel={displayText}
                  color="darkGray"
                  icon={iconType}
                  size={iconSize}
                />
              </Box>
              <Text inline size="sm">
                {this.renderValue(renderedStats[field], valueFormat)}
              </Text>
            </Box>
          ),
        )}
      </Box>
    );
  }
}
